import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { usePostHog } from '@posthog/react';
import { API_BASE_URL } from '../config';
import { setPostHogForSentry } from '../lib/sentry';
import { useToast } from '@/components/ui/toast';
import { getAuthHeaders, clearSessionToken, SESSION_TOKEN_KEY } from '../api/workosAuth';
import { setOnSessionInvalidated, requestRefresh } from '../api/authManager';
import { logAuthEvent } from '../api/authLogger';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  orgId: string;
  orgName?: string;
  /** JWT exp (Unix seconds); used to schedule proactive session check */
  expiresAt?: number;
}

export type AuthState = 'AUTH_LOADING' | 'AUTHENTICATED' | 'AUTH_REFRESHING' | 'UNAUTHENTICATED';

export interface AuthContextValue {
  user: AuthUser | null;
  authState: AuthState;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  orgId: string | null;
  orgName: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export { AuthContext };

const FIVE_MINUTES_SEC = 5 * 60;
const MIN_REFRESH_LEAD_TIME_SEC = 30;
const PROACTIVE_REFRESH_COOLDOWN_MS = 60 * 1000;

type AuthAction =
  | { type: 'SET_LOADING' }
  | { type: 'SET_AUTHENTICATED'; payload: AuthUser }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'SET_REFRESHING' };

interface AuthReducerState {
  authState: AuthState;
  user: AuthUser | null;
}

function authReducer(state: AuthReducerState, action: AuthAction): AuthReducerState {
  switch (action.type) {
    case 'SET_LOADING':
      return { authState: 'AUTH_LOADING', user: null };
    case 'SET_AUTHENTICATED':
      logAuthEvent({ event: 'auth_state_transition', state: 'AUTHENTICATED' });
      return { authState: 'AUTHENTICATED', user: action.payload };
    case 'SET_UNAUTHENTICATED':
      logAuthEvent({ event: 'auth_state_transition', state: 'UNAUTHENTICATED' });
      return { authState: 'UNAUTHENTICATED', user: null };
    case 'SET_REFRESHING':
      logAuthEvent({ event: 'auth_state_transition', state: 'AUTH_REFRESHING' });
      return { ...state, authState: 'AUTH_REFRESHING' };
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { showToast } = useToast();
  const [state, dispatch] = useReducer(authReducer, { authState: 'AUTH_LOADING', user: null });
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastProactiveRefreshAtRef = useRef<number>(0);

  useEffect(() => {
    setPostHogForSentry(posthog ?? null);
  }, [posthog]);

  const fetchMe = useCallback(async (retriedAfterFragment = false) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include', headers });
      if (res.ok) {
        const data = await res.json();
        dispatch({
          type: 'SET_AUTHENTICATED',
          payload: {
            id: data.id ?? '',
            email: data.email ?? '',
            firstName: data.firstName ?? undefined,
            lastName: data.lastName ?? undefined,
            orgId: data.orgId ?? '',
            orgName: data.orgName ?? undefined,
            expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : undefined,
          },
        });
        return;
      }
      if (res.status === 401 && !retriedAfterFragment && typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (hash.startsWith('#session=')) {
          try {
            const token = decodeURIComponent(hash.slice(9).trim());
            if (token) {
              sessionStorage.setItem(SESSION_TOKEN_KEY, token);
              await fetchMe(true);
              return;
            }
          } catch {
            // fall through to clear and unauthenticated
          }
        }
      }
      if (res.status !== 401) {
        console.warn('[Auth] /auth/me returned unexpected status:', res.status);
      }
      clearSessionToken();
      dispatch({ type: 'SET_UNAUTHENTICATED' });
    } catch (err) {
      console.warn('[Auth] /auth/me fetch failed:', err);
      clearSessionToken();
      dispatch({ type: 'SET_UNAUTHENTICATED' });
    }
  }, []);

  // Single authority: only /auth/me determines auth state. Session invalidation from authManager (refresh failed) drives logout.
  useEffect(() => {
    const handleSessionInvalidated = () => {
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      navigate('/login', { replace: true });
    };
    setOnSessionInvalidated(handleSessionInvalidated);
    return () => setOnSessionInvalidated(null);
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => fetchMe(), 0);
    return () => clearTimeout(t);
  }, [fetchMe]);

  useEffect(() => {
    if (state.user) {
      posthog?.identify(state.user.id, {
        email: state.user.email,
        name: [state.user.firstName, state.user.lastName].filter(Boolean).join(' ') || undefined,
        organisation: state.user.orgName ?? state.user.orgId,
      });
      posthog?.capture('user_logged_in');
      Sentry.setUser({ id: state.user.id, email: state.user.email ?? undefined, orgId: state.user.orgId, orgName: state.user.orgName ?? undefined });
      posthog?.onSessionId((sessionId) => {
        Sentry.setExtra('posthog_session_id', sessionId);
        Sentry.setExtra('posthog_recording_url', `https://app.posthog.com/recordings/${sessionId}`);
      });
    } else {
      Sentry.setUser(null);
    }
  }, [state.user, posthog]);

  const logout = useCallback(async () => {
    logAuthEvent({ event: 'logout', reason: 'user_initiated' });
    posthog?.reset();
    Sentry.setUser(null);
    // Capture auth headers BEFORE clearing the session token so the Bearer is included in the
    // POST /logout request (used by the backend to extract session_id for the WorkOS logout URL).
    const loginPath = '/login';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const authHeaders = await getAuthHeaders();
      clearSessionToken();
      const res = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      const logoutUrl = data?.logout_url;
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      // Store WorkOS logout URL (includes session_id) so LoginPage can redirect to it after
      // we do a full-page load to /login (so the cookie clear is applied).
      if (typeof sessionStorage !== 'undefined' && logoutUrl) {
        sessionStorage.setItem('pending_workos_logout', logoutUrl);
      }
      const target = origin ? `${origin}${loginPath}?from_logout=1` : loginPath;
      if (origin) {
        window.location.href = target;
      } else {
        navigate(loginPath, { replace: true });
      }
    } catch {
      clearSessionToken();
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      if (origin) {
        window.location.href = `${origin}${loginPath}`;
      } else {
        navigate(loginPath, { replace: true });
      }
    }
  }, [navigate, posthog]);

  const logoutRef = useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  /** Proactive token expiry: 5 min before expiry run single-flight refresh via authManager. */
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    const user = state.user;
    if (!user?.expiresAt) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = user.expiresAt - nowSec;
    const refreshLeadTimeSec = Math.min(
      FIVE_MINUTES_SEC,
      Math.max(MIN_REFRESH_LEAD_TIME_SEC, Math.floor(secondsUntilExpiry / 2))
    );
    const fireAtSec = user.expiresAt - refreshLeadTimeSec;
    let delayMs = Math.max(0, (fireAtSec - nowSec) * 1000);

    // If tokens are issued with a short lifetime, a fixed 5-minute window causes an
    // immediate refresh loop (refresh -> /auth/me -> refresh). Enforce a minimum cooldown.
    const msSinceLastProactiveRefresh = Date.now() - lastProactiveRefreshAtRef.current;
    if (delayMs === 0 && msSinceLastProactiveRefresh < PROACTIVE_REFRESH_COOLDOWN_MS) {
      delayMs = PROACTIVE_REFRESH_COOLDOWN_MS - msSinceLastProactiveRefresh;
    }

    expiryTimerRef.current = setTimeout(async () => {
      expiryTimerRef.current = null;
      lastProactiveRefreshAtRef.current = Date.now();
      const ok = await requestRefresh();
      if (ok) {
        await fetchMe();
      } else {
        showToast('error', 'Your session has expired. Please sign in again.');
        await logoutRef.current();
      }
    }, delayMs);

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [state.user?.id, state.user?.expiresAt, fetchMe, showToast]);

  const isLoading = state.authState === 'AUTH_LOADING' || state.authState === 'AUTH_REFRESHING';
  const value: AuthContextValue = {
    user: state.user,
    authState: state.authState,
    isLoading,
    isAuthenticated: !!state.user,
    logout,
    orgId: state.user?.orgId ?? null,
    orgName: state.user?.orgName ?? null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- useAuth is hook, AuthProvider is component
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
